import { useEffect, useState } from "react";
import { toast } from "react-toastify";
import apis from "../../apis";
import backgroundImg from "../../assets/images/contactUsPageBackground.jpeg";
import { useAuth } from "../../context/DataContext";
import "./Login.css";

// Inline SVG icons matching the prototype's icon set
const IconEye = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M2.5 12c2.6-4.6 6-7 9.5-7s6.9 2.4 9.5 7c-2.6 4.6-6 7-9.5 7S5.1 16.6 2.5 12z" />
        <circle cx="12" cy="12" r="2.6" />
    </svg>
);

const IconEyeOff = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M3 3l18 18" />
        <path d="M10.7 6.2A10 10 0 0 1 12 6c4 0 7.4 2.3 9.5 6-.7 1.3-1.6 2.4-2.6 3.3" />
        <path d="M6.5 7.6C4.7 8.9 3.2 10.7 2.5 12c2.1 3.7 5.5 6 9.5 6 1.4 0 2.7-.3 3.9-.8" />
        <path d="M9.9 9.9a3 3 0 0 0 4.2 4.2" />
    </svg>
);

const IconArrowRight = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 12h16" />
        <path d="M14 6l6 6-6 6" />
    </svg>
);

const IconGoogle = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 9.5h5.2c.2.7.3 1.5.3 2.5 0 3.6-2.4 6-5.5 6" />
    </svg>
);

const IconWarn = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 3l10 18H2z" />
        <path d="M12 10v5" />
        <circle cx="12" cy="18" r=".7" fill="currentColor" />
    </svg>
);

const IconClock = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="9" />
        <path d="M12 7v5l3 3" />
    </svg>
);

const IconLock = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
        <rect x="5" y="11" width="14" height="10" rx="2" />
        <path d="M8 11V7a4 4 0 1 1 8 0v4" />
    </svg>
);

/**
 * Format remaining time until deadline
 */
const formatTimeRemaining = (deadlineStr) => {
    if (!deadlineStr) return null;
    const deadline = new Date(deadlineStr);
    const now = new Date();
    const diffMs = deadline - now;
    if (diffMs <= 0) return null;

    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours >= 48) {
        const days = Math.floor(hours / 24);
        return `${days} day${days > 1 ? 's' : ''} remaining`;
    }
    if (hours >= 1) {
        return `${hours}h ${minutes}m remaining`;
    }
    return `${minutes}m remaining`;
};

/**
 * Check if deadline is within 24 hours from now
 */
const isDeadlineUrgent = (deadlineStr) => {
    if (!deadlineStr) return false;
    const deadline = new Date(deadlineStr);
    const now = new Date();
    const diffMs = deadline - now;
    return diffMs > 0 && diffMs <= 24 * 60 * 60 * 1000;
};

/**
 * Check if deadline has passed
 */
const isDeadlinePassed = (deadlineStr) => {
    if (!deadlineStr) return false;
    const deadline = new Date(deadlineStr);
    return new Date() >= deadline;
};

const Login = () => {
    const { signInWithGoogle, signInWithEmail } = useAuth();

    // State
    const [loading, setLoading] = useState(false);
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState("");
    const [juryDeadlines, setJuryDeadlines] = useState(null);

    // Fetch global settings to get jury deadlines
    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const response = await apis.systemSettings.getGlobalSettings();
                if (response?.data?.juryDeadlines) {
                    setJuryDeadlines(response.data.juryDeadlines);
                }
            } catch (err) {
                console.error("Failed to fetch jury deadlines:", err);
            }
        };
        fetchSettings();
    }, []);

    // Check if ANY deadline is approaching or has passed (for the login screen info)
    const hasAnyExpiredDeadline = juryDeadlines
        ? Object.values(juryDeadlines).some(d => isDeadlinePassed(d))
        : false;

    const hasAnyUrgentDeadline = juryDeadlines
        ? Object.values(juryDeadlines).some(d => isDeadlineUrgent(d))
        : false;

    // Build a summary of deadlines for display
    const deadlineSummary = juryDeadlines
        ? Object.entries(juryDeadlines)
            .filter(([, d]) => !isDeadlinePassed(d))
            .map(([cat, d]) => ({
                category: cat,
                remaining: formatTimeRemaining(d),
                urgent: isDeadlineUrgent(d),
            }))
            .filter(d => d.remaining)
        : [];

    const expiredCategories = juryDeadlines
        ? Object.entries(juryDeadlines)
            .filter(([, d]) => isDeadlinePassed(d))
            .map(([cat]) => cat)
        : [];

    // --- Handlers ---
    const handleEmailLogin = async (e) => {
        e.preventDefault();

        if (!email.trim() || !password.trim()) {
            setError("Please fill in all required fields.");
            return;
        }

        try {
            setLoading(true);
            setError("");
            await signInWithEmail(email, password);
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleGoogleLogin = async () => {
        try {
            setLoading(true);
            setError("");
            await signInWithGoogle();
        } catch (err) {
            setError(err.message);
            toast.error(err.message);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="login-screen">
            {/* Left Panel — Image */}
            <div className="login-image-panel">
                <img src={backgroundImg} alt="APCS event" className="login-image-bg" />
                <div className="login-image-overlay">
                    <div className="login-image-content">
                        <div className="login-image-badge">APCS 2026</div>
                        <h1 className="login-image-title">Jury Scoring Platform</h1>
                        <p className="login-image-subtitle">
                            Asia Pacific Chopin Society — Adjudicator Portal
                        </p>
                    </div>
                </div>
            </div>

            {/* Right Panel — Login Form */}
            <div className="login-form-panel">
                <div className="login-wrapper">
                    {/* Logo Block */}
                    <div className="login-logo-block">
                        <div className="login-logo-icon">A</div>
                        <div className="login-logo-title">APCS Scoring Platform</div>
                        <div className="login-logo-subtitle">jury portal · sign in</div>
                    </div>

                    {/* Deadline Info Banners */}
                    {hasAnyExpiredDeadline && expiredCategories.length > 0 && (
                        <div className="login-deadline-banner expired">
                            <IconLock />
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Scoring period has ended</div>
                                <div>
                                    {expiredCategories.length === 1
                                        ? `The deadline for ${expiredCategories[0]} has passed.`
                                        : `Deadlines have passed for: ${expiredCategories.join(', ')}.`
                                    }
                                </div>
                                <div style={{ marginTop: 4, fontSize: 11, opacity: 0.8 }}>
                                    If you are a jury member for these categories, you can no longer submit scores. Contact admin for assistance.
                                </div>
                            </div>
                        </div>
                    )}

                    {hasAnyUrgentDeadline && deadlineSummary.some(d => d.urgent) && (
                        <div className="login-deadline-banner warning">
                            <IconClock />
                            <div>
                                <div style={{ fontWeight: 600, marginBottom: 4 }}>Deadline approaching</div>
                                {deadlineSummary.filter(d => d.urgent).map(d => (
                                    <div key={d.category} style={{ fontSize: 12, marginTop: 2 }}>
                                        <strong>{d.category}</strong>: {d.remaining}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Form Card */}
                    <form className="login-card" onSubmit={handleEmailLogin}>
                        {/* Error Banner */}
                        {error && (
                            <div className="login-error-banner">
                                <IconWarn />
                                <div>{error}</div>
                            </div>
                        )}

                        {/* Email Field */}
                        <label className="login-label" htmlFor="login-email">Email</label>
                        <input
                            id="login-email"
                            type="email"
                            className="login-input"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); if (error) setError(""); }}
                            placeholder="you@school.edu"
                            autoFocus
                        />

                        {/* Password Field */}
                        <div className="login-password-row">
                            <span className="login-label" style={{ marginBottom: 0 }}>Password</span>
                        </div>
                        <div className="login-input-group">
                            <input
                                id="login-password"
                                type={showPassword ? "text" : "password"}
                                value={password}
                                onChange={(e) => { setPassword(e.target.value); if (error) setError(""); }}
                                placeholder="••••••••"
                            />
                            <button
                                type="button"
                                className="login-iconbtn"
                                onClick={() => setShowPassword(s => !s)}
                                title={showPassword ? "Hide password" : "Show password"}
                            >
                                {showPassword ? <IconEyeOff /> : <IconEye />}
                            </button>
                        </div>

                        {/* Sign In Button */}
                        <button
                            type="submit"
                            className="login-btn primary"
                            disabled={loading}
                        >
                            {loading ? (
                                <span className="login-spinner" />
                            ) : (
                                <>Sign in <IconArrowRight /></>
                            )}
                        </button>

                        {/* Divider */}
                        <div className="login-divider">
                            <div className="login-divider-line" />
                            <span className="login-divider-text">or</span>
                            <div className="login-divider-line" />
                        </div>

                        {/* Google Sign In — always available */}
                        <button
                            type="button"
                            className="login-btn"
                            onClick={handleGoogleLogin}
                            disabled={loading}
                        >
                            <IconGoogle /> Continue with Google
                        </button>
                    </form>

                    {/* Footer */}
                    <div className="login-footer">
                        By signing in you agree to our terms · privacy
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Login;